import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import { clearState, updateUsers, usersRecord } from "../../redux/Slices/UsersSlice";
import { fetchSections } from "../../redux/Slices/SectionSlice";
import { fetchCities } from "../../redux/Slices/CitiesSlice";
import { fetchGrades } from "../../redux/Slices/GradesSlice";

const EditUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    parent_phone: "",
    grade_id: "",
    section_id: "",
    city_id: "",
    status: "",
    password: "",
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { isLoading: loading, error, success, record } = useSelector((state) => state.users);
  const { cities } = useSelector((state) => state.cities);
  const { grades } = useSelector((state) => state.grades);
  const { data: sections } = useSelector((state) => state.sections);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchCities());
    dispatch(fetchGrades());
    dispatch(fetchSections());
    dispatch(usersRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (record) {
      const original = {
        name: record?.name || "",
        phone: record?.phone || "",
        parent_phone: record?.parent_phone || "",
        grade_id: record?.grade_id || record?.grade?.id || "",
        section_id: record?.section_id || record?.section?.id || "",
        city_id: record?.city_id || record?.city?.id || "",
        status: (record?.status === "active" || record?.status === true) ? "1" : "0",
        password: "",
      };
      setOriginalData(original);
      setFormData(original);
    }
  }, [record]);

  // Check for differences
  useEffect(() => {
    if (originalData) {
      const hasFormChanges =
        formData.name !== originalData.name ||
        formData.phone !== originalData.phone ||
        formData.parent_phone !== originalData.parent_phone ||
        formData.grade_id !== originalData.grade_id ||
        formData.section_id !== originalData.section_id ||
        formData.city_id !== originalData.city_id ||
        formData.status !== originalData.status ||
        formData.password !== "" ||
        !!formData.image;

      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  useEffect(() => {
    if (success) {
      toast.success("تم التحديث بنجاح");
      dispatch(clearState());
      navigate("/users");
    }
    if (error) {
      if (error.errors) {
        const firstError = Object.values(error.errors)[0][0];
        if (firstError.includes("phone")) {
          toast.error("رقم الهاتف مستخدم من قبل");
        } else {
          toast.error(firstError);
        }
      } else {
        toast.error("لم يتم التحديث");
      }
      dispatch(clearState());
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone' || name === 'parent_phone') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else if (name === 'name') {
      const letterValue = value.replace(/[^a-zA-Z\u0600-\u06FF\s]/g, '');
      setFormData((prev) => ({ ...prev, name: letterValue }));
    } else if (name === 'grade_id') {
      setFormData((prev) => ({ ...prev, grade_id: value, section_id: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const filteredCities = cities?.data
    ? cities.data.filter(c => {
        const name = (c.name_ar || c.name_en || "").toLowerCase();
        return !name.includes("test") && !name.includes("asd") && !name.includes("check") && !name.includes("تجرب");
      })
    : [];

  const filteredGradesList = grades?.data
    ? grades.data.filter(g => {
        const name = (g.name_ar || g.name_en || "").toLowerCase();
        return !name.includes("test") && !name.includes("asd") && !name.includes("check") && !name.includes("تجرب");
      })
    : [];

  const selectedGradeObj = grades?.data?.find(g => String(g.id) === String(formData.grade_id));
  const isMiddleSchool = selectedGradeObj && (
    (selectedGradeObj.name_ar || "").includes("إعدادي") ||
    (selectedGradeObj.name_en || "").toLowerCase().includes("middle") ||
    (selectedGradeObj.name_en || "").toLowerCase().includes("prep")
  );

  const filteredSections = sections && sections.length > 0
    ? Array.from(new Map(
        sections
          .filter((s) => String(s.grade_id) === String(formData.grade_id))
          .filter(s => {
            const name = (s.name_ar || s.name_en || "").toLowerCase();
            return !name.includes("test") && !name.includes("asd") && !name.includes("check") && !name.includes("تجرب");
          })
          .map((s) => [s.name_ar, s])
      ).values())
    : [];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("الملف يجب أن يكون صورة (jpg, jpeg, png)");
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [e.target.name]: file,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("الرجاء إدخال الاسم كامل");
      return;
    }
    const nameRegex = /^[a-zA-Z\u0600-\u06FF\s]+$/;
    if (!nameRegex.test(formData.name.trim())) {
      toast.error("الاسم كامل يجب أن يحتوي على حروف فقط");
      return;
    }

    if (formData.phone && formData.phone.length < 11) {
      toast.error("رقم الهاتف يجب أن يكون 11 رقماً");
      return;
    }

    if (formData.parent_phone && formData.parent_phone.length < 11) {
      toast.error("رقم هاتف ولي الأمر يجب أن يكون 11 رقماً");
      return;
    }

    dispatch(clearState());

    const data = new FormData();

    if (formData.name !== originalData?.name) {
      data.append("name", formData.name);
    }
    if (formData.phone !== originalData?.phone) {
      data.append("phone", formData.phone);
    }
    if (formData.parent_phone !== originalData?.parent_phone) {
      data.append("parent_phone", formData.parent_phone);
    }
    if (formData.password && formData.password.trim() !== "") {
      data.append("password", formData.password);
    }
    if (formData.grade_id !== originalData?.grade_id) {
      data.append("grade_id", formData.grade_id);
    }
    if (formData.section_id !== originalData?.section_id) {
      data.append("section_id", formData.section_id);
    }
    if (formData.city_id !== originalData?.city_id) {
      data.append("city_id", formData.city_id);
    }
    if (formData.status !== originalData?.status) {
      data.append("status", formData.status);
    }
    if (formData.image) {
      data.append("image", formData.image);
    }

    dispatch(updateUsers({ id, data }));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--main-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .user-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .user-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            👤 تعديل حساب الطالب
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل تفاصيل وحالة الطالب {record?.name ? `(${record.name})` : ""}</p>
        </div>
        <Link to="/users" className="text-decoration-none">
          <button style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            <FaArrowLeft size={12} /> العودة
          </button>
        </Link>
      </div>

      {/* ── Form Card ─────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px 30px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>الاسم كاملاً</label>
              <input
                type="text"
                style={inputStyle}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل الاسم الرباعي للطالب"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>رقم الهاتف</label>
              <input
                type="tel"
                style={inputStyle}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل رقم الهاتف المحمول"
                pattern="[0-9]*"
                inputMode="numeric"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>رقم هاتف ولي الأمر</label>
              <input
                type="tel"
                style={inputStyle}
                name="parent_phone"
                value={formData.parent_phone}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل رقم ولي الأمر للطوارئ"
                pattern="[0-9]*"
                inputMode="numeric"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>كلمة المرور الجديدة (اختياري)</label>
              <input
                type="password"
                style={inputStyle}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل كلمة مرور جديدة أو اتركها فارغة"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>تحديث صورة الطالب</label>
              <input
                type="file"
                style={inputStyle}
                name="image"
                onChange={handleFileChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
              />
              {record?.image && (
                <div className="mt-2">
                  <small className="text-muted">الصورة الحالية:</small>
                  <br />
                  <img
                    src={record.image}
                    alt="Current user"
                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: "1.5px solid #cbd5e1" }}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>المدينة</label>
              <select
                className="user-custom-select"
                name="city_id"
                value={formData.city_id ? String(formData.city_id) : ""}
                onChange={handleInputChange}
              >
                <option value="" disabled>اختر مدينة</option>
                {filteredCities.map((city) => (
                  <option key={city.id} value={String(city.id)}>{city.name_ar}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>الصف الدراسي</label>
              <select
                className="user-custom-select"
                name="grade_id"
                value={formData.grade_id ? String(formData.grade_id) : ""}
                onChange={handleInputChange}
              >
                <option value="" disabled>اختر صف</option>
                {filteredGradesList.map((grade) => (
                  <option key={grade.id} value={String(grade.id)}>{grade.name_ar}</option>
                ))}
              </select>
            </div>
            {!isMiddleSchool && (
              <div className="col-md-6 mb-3">
                <label style={labelStyle}>القسم الدراسي</label>
                <select
                  className="user-custom-select"
                  name="section_id"
                  value={formData.section_id ? String(formData.section_id) : ""}
                  onChange={handleInputChange}
                  required={filteredSections.length > 0 && !isMiddleSchool}
                  disabled={!formData.grade_id}
                >
                  <option value="" disabled>
                    {!formData.grade_id ? "اختر الصف الدراسي أولاً" : "اختر قسم"}
                  </option>
                  {filteredSections.map((section) => (
                    <option key={section.id} value={String(section.id)}>{section.name_ar}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>حالة التفعيل</label>
              <select
                className="user-custom-select"
                name="status"
                value={formData.status ? String(formData.status) : ""}
                onChange={handleInputChange}
              >
                <option value="" disabled>اختر الحالة</option>
                <option value="0">غير نشط</option>
                <option value="1">نشط / مفعل</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f1f5f9", marginTop: 20, paddingTop: 20 }}>
            <button type="button" onClick={() => navigate("/users")} style={{ padding: "9px 22px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
            <button type="submit" disabled={loading || !hasChanges} style={{ padding: "9px 28px", borderRadius: 9, border: "none", background: hasChanges ? "linear-gradient(135deg,var(--main-color),var(--main-light))" : "#cbd5e1", color: "#fff", fontWeight: 700, cursor: hasChanges ? "pointer" : "not-allowed" }}>
              {loading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;
